import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import SOURCE_TYPE from '../SourceCreate.constants';
import { useSourceCreateState, useSourceDialog } from '../SourceCreate.hooks';
import {
  type AlertDeviceField,
  type ConnectionInputSchema,
  SourceDialogMode
} from '../SourceCreate.types';

/**
 * Manages default value initialization for the source form.
 *
 * Responsibilities:
 * - Set default categories in New mode
 * - Initialize default settings (status, fetch flags)
 * - Configure authentication defaults for Microsoft sources
 * - Set default alert fields for Medigate sources
 * - Backfill alert fields for legacy Medigate sources
 * - Initialize cloud providers and regions
 * - Configure time intervals for Juniper Mist
 *
 * @param connectionInputSchema - Dynamic form schema
 */
export const useSourceDefaults = (
  connectionInputSchema: ConnectionInputSchema
) => {
  const { setValue } = useFormContext();

  /**
   * Performance Optimization: Track schema version to prevent unnecessary effect runs.
   * Only executes when source type actually changes, not when schema object
   * reference changes.
   */
  const lastProcessedSchemaVersion = useRef<{
    sourceTypeId: number;
    mode: string;
    hasSchema: boolean;
  }>({ sourceTypeId: 0, mode: '', hasSchema: false });

  const {
    categoryMasterList,
    selectedSourceTypeId,
    selectedSourceDetails,
    selectedSubSourceType,
    setCategories,
    setSourceStatus,
    setEnableFetchDeviceVulnerabilities,
    setEnableFetchObjects,
    setCloudProvider: setCloudProviders,
    setRegion: setRegions,
    setSelectedTimeInterval,
    setVulnFields,
    setDeviceAlertFields
  } = useSourceCreateState();

  const { mode, setAuthenticationMode, setNewCertificate } = useSourceDialog();

  /**
   * Helper function to set default vulnerability and device alert fields for Medigate sources.
   *
   * Business Context:
   * - Medigate sources require all vulnerability and alert fields to be selected by default
   * - In New mode, this provides a sensible starting configuration
   * - In Edit mode for old migrated sources, these fields may be null because they were
   *   added after initial source creation. We backfill them to maintain consistency.
   */
  const setDefaultAlertFields = () => {
    // Select all available vulnerability fields from the schema
    const deviceVulnerabilities =
      connectionInputSchema?.fetch_device_vulnerabilities?.[4]?.options ?? [];
    setVulnFields(deviceVulnerabilities);
    setValue('vuln_fields', deviceVulnerabilities);

    // Select all available device alert fields from the schema
    const options = connectionInputSchema?.device_alert_fields?.options;
    const deviceFields: AlertDeviceField[] = [];

    options?.forEach((row) => {
      const devAlertRow = row as unknown as AlertDeviceField;
      deviceFields.push(devAlertRow);
    });

    setDeviceAlertFields(deviceFields);
    setValue('device_alert_fields', deviceFields);
  };

  /**
   * Effect: Set default categories when creating a new source.
   *
   * Business Rules:
   * 1. In New mode, all available categories are selected by default
   * 2. Microsoft Entra ID: Categories are filtered by API endpoint (Graph API vs O365 API)
   *    - Graph API sources only see categories with 'graph' in the name
   *    - O365 API sources only see categories with 'o365' in the name
   */
  useEffect(() => {
    // Start with all categories selected
    const selectedAllCategories = categoryMasterList.map((item) => item.name);

    // Set default categories in New mode
    if (mode === SourceDialogMode.New) {
      setCategories(selectedAllCategories || []);
      setValue('category', selectedAllCategories);
    }
  }, [
    mode,
    categoryMasterList,
    selectedSourceTypeId,
    selectedSubSourceType,
    setCategories,
    setValue
  ]);

  /**
   * Effect: Initialize default values and settings when creating or editing sources.
   *
   * Performance: Uses ref-based schema version tracking to prevent running on every
   * render. Only executes when source type actually changes, not when schema object
   * reference changes.
   *
   * Handles multiple initialization scenarios:
   * 1. New mode defaults (status, fetch flags, authentication)
   * 2. Source-specific defaults (Medigate alert fields, Microsoft authentication)
   * 3. Legacy source backfill (Medigate sources created before alert fields)
   * 4. Cloud provider/region initialization
   * 5. Time interval configuration (Juniper Mist)
   */
  useEffect(() => {
    // Performance Guard: Skip if schema hasn't actually changed
    // This prevents running initialization logic on every render when
    // connectionInputSchema object reference changes but content is the same
    const hasSchema = !!(
      connectionInputSchema?.fetch_device_vulnerabilities?.[4]?.options ||
      connectionInputSchema?.device_alert_fields?.options ||
      connectionInputSchema?.cloud_provider_inputs ||
      connectionInputSchema?.region_inputs ||
      connectionInputSchema?.source_inputs
    );

    const currentVersion = {
      sourceTypeId: selectedSourceTypeId,
      mode,
      hasSchema
    };

    const hasSchemaChanged =
      currentVersion.sourceTypeId !==
        lastProcessedSchemaVersion.current.sourceTypeId ||
      currentVersion.mode !== lastProcessedSchemaVersion.current.mode ||
      (currentVersion.hasSchema !==
        lastProcessedSchemaVersion.current.hasSchema &&
        currentVersion.hasSchema);

    if (!hasSchemaChanged) {
      return;
    }

    // Update version tracker
    lastProcessedSchemaVersion.current = currentVersion;

    // New Mode: Initialize default settings
    if (mode === SourceDialogMode.New) {
      setSourceStatus(true);
      setEnableFetchDeviceVulnerabilities(false);
      setEnableFetchObjects(false);
    }

    // Microsoft Sources: Reset authentication mode to default in New mode
    if (
      mode === SourceDialogMode.New &&
      selectedSourceTypeId === SOURCE_TYPE.MICROSOFT
    ) {
      setAuthenticationMode('client_secret');
      setNewCertificate(false);
    }

    // Medigate New Mode: Select all vulnerability and device alert fields by default
    // Guard: Only set defaults if the schema is loaded with required data
    if (
      mode === SourceDialogMode.New &&
      selectedSourceTypeId === SOURCE_TYPE.MEDIGATE &&
      connectionInputSchema?.fetch_device_vulnerabilities?.[4]?.options &&
      connectionInputSchema?.device_alert_fields?.options
    ) {
      setDefaultAlertFields();
    }

    // Medigate Edit Mode: Backfill alert fields for legacy sources
    // Old migrated Medigate sources have null alert_device_fields, alert_fields, and vuln_fields
    // because these fields were added after initial source creation. Events still ingest correctly,
    // but we need to populate these fields when editing to maintain consistency.
    if (
      mode === SourceDialogMode.Edit &&
      Number(selectedSourceDetails?.source_type_id) === SOURCE_TYPE.MEDIGATE &&
      selectedSourceDetails?.alert_device_fields === null &&
      selectedSourceDetails?.alert_fields === null &&
      selectedSourceDetails?.vuln_fields === null &&
      connectionInputSchema?.fetch_device_vulnerabilities?.[4]?.options &&
      connectionInputSchema?.device_alert_fields?.options
    ) {
      setDefaultAlertFields();
    }

    // Cloud Provider: Set default cloud providers in New mode if available
    if (
      mode === SourceDialogMode.New &&
      Array.isArray(connectionInputSchema?.cloud_provider_inputs) &&
      connectionInputSchema?.cloud_provider_inputs?.length > 0
    ) {
      const options =
        connectionInputSchema?.cloud_provider_inputs?.[0]?.options ?? [];
      setCloudProviders(options);
      setValue('cloud_provider', options);
    }

    // Region: Clear regions in New mode if region inputs are available
    if (
      mode === SourceDialogMode.New &&
      Array.isArray(connectionInputSchema?.region_inputs) &&
      connectionInputSchema?.region_inputs?.length > 0
    ) {
      setRegions([]);
      setValue('region', []);
    }

    // Juniper Mist: Set default time interval and populate sub-time interval options
    if (
      mode === SourceDialogMode.New &&
      connectionInputSchema?.source_inputs &&
      selectedSourceTypeId === SOURCE_TYPE.JUNIPER_MIST &&
      connectionInputSchema?.show_sub_time_interval &&
      connectionInputSchema?.sub_time_interval_inputs
    ) {
      const timeIntervalInputs = connectionInputSchema?.source_inputs?.find(
        (item) => item.name === 'time_interval'
      );
      let timeIntervalValue = timeIntervalInputs?.value;
      if (Array.isArray(timeIntervalValue)) {
        timeIntervalValue = timeIntervalValue[0] || '';
      }
      setSelectedTimeInterval(timeIntervalValue || '');
    }
  }, [
    mode,
    selectedSourceTypeId,
    selectedSourceDetails,
    connectionInputSchema,
    setSourceStatus,
    setEnableFetchDeviceVulnerabilities,
    setEnableFetchObjects,
    setAuthenticationMode,
    setNewCertificate,
    setCloudProviders,
    setRegions,
    setValue,
    setSelectedTimeInterval
  ]);
};
